import { Buffer } from 'buffer';
import { Networks, StrKey, Contract, scValToNative } from '@stellar/stellar-sdk';
// no-axios/contract: CRA/webpack breaks axios.create in the default RPC client
import { Client } from '@stellar/stellar-sdk/no-axios/contract';
import { Server } from '@stellar/stellar-sdk/no-axios/rpc';
import type { xdr } from '@stellar/stellar-sdk';
import { NetworkConfig } from '../config/networks';

if (typeof window !== 'undefined') {
  (window as any).Buffer = (window as any).Buffer || Buffer;
}

/** Dummy account used only for read-only simulations. */
const SIM_PUBLIC_KEY = 'GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF';

const READ_ONLY_OR_HELPER_SUFFIXES = ['_intent_data'];
const EXCLUDED_FUNCTION_NAMES = new Set([
  '__constructor',
  'validation_engine',
]);

export function isStellarContractAddress(address: string): boolean {
  const trimmed = address.trim();
  if (!trimmed) return false;
  try {
    return StrKey.isValidContract(trimmed) || StrKey.isValidEd25519PublicKey(trimmed);
  } catch {
    return false;
  }
}

export function normalizeStellarAddress(address: string): string {
  return address.trim().toUpperCase();
}

function networkPassphraseFor(chain: NetworkConfig): string {
  if (chain.networkPassphrase) return chain.networkPassphrase;
  switch (chain.chainId) {
    case 1:
      return Networks.PUBLIC;
    case 3:
      return Networks.FUTURENET;
    case 2:
    default:
      return Networks.TESTNET;
  }
}

async function getContractClient(
  chain: NetworkConfig,
  contractId: string,
  publicKey: string = SIM_PUBLIC_KEY
): Promise<Client> {
  return Client.from({
    contractId: normalizeStellarAddress(contractId),
    networkPassphrase: networkPassphraseFor(chain),
    rpcUrl: chain.rpcUrl,
    publicKey,
  });
}

function scSpecTypeToString(typeDef: xdr.ScSpecTypeDef): string {
  const kind = typeDef.switch().name;
  switch (kind) {
    case 'scSpecTypeBool':
      return 'bool';
    case 'scSpecTypeU32':
      return 'u32';
    case 'scSpecTypeI32':
      return 'i32';
    case 'scSpecTypeU64':
      return 'u64';
    case 'scSpecTypeI64':
      return 'i64';
    case 'scSpecTypeU128':
      return 'u128';
    case 'scSpecTypeI128':
      return 'i128';
    case 'scSpecTypeU256':
      return 'u256';
    case 'scSpecTypeI256':
      return 'i256';
    case 'scSpecTypeAddress':
      return 'address';
    case 'scSpecTypeSymbol':
      return 'symbol';
    case 'scSpecTypeString':
      return 'string';
    case 'scSpecTypeBytes':
      return 'bytes';
    case 'scSpecTypeBytesN':
      return `bytesN`;
    case 'scSpecTypeVec':
      return `vec<${scSpecTypeToString(typeDef.vec().elementType())}>`;
    case 'scSpecTypeMap':
      return `map<${scSpecTypeToString(typeDef.map().keyType())},${scSpecTypeToString(typeDef.map().valueType())}>`;
    case 'scSpecTypeOption':
      return `option<${scSpecTypeToString(typeDef.option().valueType())}>`;
    case 'scSpecTypeResult':
      return `result<${scSpecTypeToString(typeDef.result().okType())},${scSpecTypeToString(typeDef.result().errorType())}>`;
    case 'scSpecTypeTuple':
      return `(${typeDef.tuple().valueTypes().map(scSpecTypeToString).join(',')})`;
    case 'scSpecTypeUdt':
      return typeDef.udt().name().toString();
    default:
      return kind.replace(/^scSpecType/, '').toLowerCase();
  }
}

export interface StellarFunctionSpec {
  name: string;
  inputs: { name: string; type: string }[];
  prototype: string;
  /** Display signature with param names, for policy mapping UI */
  displaySignature: string;
}

function shouldIncludeFunction(name: string): boolean {
  if (EXCLUDED_FUNCTION_NAMES.has(name)) return false;
  if (name.startsWith('__')) return false;
  if (READ_ONLY_OR_HELPER_SUFFIXES.some(suffix => name.endsWith(suffix))) return false;
  return true;
}

/**
 * Fetch SEP-48 contract interface from on-chain WASM (contractspecv0).
 */
export async function fetchStellarContractFunctions(
  chain: NetworkConfig,
  contractId: string
): Promise<StellarFunctionSpec[]> {
  const client = await getContractClient(chain, contractId);
  return client.spec
    .funcs()
    .map((fn: xdr.ScSpecFunctionV0) => {
      const name = fn.name().toString();
      const inputs = fn.inputs().map((input: xdr.ScSpecFunctionInputV0) => ({
        name: input.name().toString(),
        type: scSpecTypeToString(input.type()),
      }));
      const typeList = inputs.map((i: { type: string }) => i.type).join(',');
      const namedParams = inputs.map((i: { name: string; type: string }) => `${i.type} ${i.name}`.trim()).join(', ');
      return {
        name,
        inputs,
        prototype: `${name}(${typeList})`,
        displaySignature: `${name}(${namedParams})`,
      };
    })
    .filter((fn: StellarFunctionSpec) => shouldIncludeFunction(fn.name));
}

/**
 * Characteristic Trustline Firewall methods (SEP-48).
 * See trustline-firewall: target + forward (+ owner / public_forward).
 */
const FIREWALL_REQUIRED_METHODS = ['target', 'forward'] as const;

export function isStellarTrustlineFirewallSpec(functionNames: string[]): boolean {
  const names = new Set(functionNames);
  return FIREWALL_REQUIRED_METHODS.every(name => names.has(name));
}

export interface StellarContractInspection {
  isTrustlineFirewall: boolean;
  /** Address whose SEP-48 should drive policy mapping (target if firewall, else the dApp). */
  specContractAddress: string;
  /** Protected target when isTrustlineFirewall; otherwise null. */
  firewallTargetAddress: string | null;
  validationEngineAddress: string;
  registryAddress: string;
}

/**
 * Trustline SDK stores the VE under instance key symbol "VE" (VE_KEY).
 * This is the canonical lookup for any protected contract (getter is irrelevant).
 */
async function readValidationEngineAddress(
  chain: NetworkConfig,
  contractId: string
): Promise<string> {
  const server = new Server(chain.rpcUrl);
  const footprint = new Contract(normalizeStellarAddress(contractId)).getFootprint();
  const response = await server.getLedgerEntries(footprint);
  if (!response.entries?.length) {
    throw new Error(
      'No validation engine found for this contract. ' +
        'Call set_validation_engine in the constructor (trustline-sdk) and redeploy.'
    );
  }

  const instance = response.entries[0].val.contractData().val().instance();
  const storage = instance.storage();
  if (storage) {
    for (const entry of storage) {
      const key = entry.key();
      if (key.switch().name !== 'scvSymbol') continue;
      if (key.sym().toString() !== 'VE') continue;
      const native = scValToNative(entry.val());
      const address = normalizeStellarAddress(String(native || ''));
      if (StrKey.isValidContract(address)) {
        return address;
      }
    }
  }

  throw new Error(
    'No validation engine found for this contract. ' +
      'Call set_validation_engine in the constructor (trustline-sdk) and redeploy.'
  );
}

/**
 * Inspect a Stellar dApp: detect Trustline Firewall, resolve target if any,
 * then resolve and verify the Validation Engine (instance key "VE") + registry().
 */
export async function inspectStellarProtectedContract(
  chain: NetworkConfig,
  contractId: string
): Promise<StellarContractInspection> {
  const normalized = normalizeStellarAddress(contractId);
  const dappClient = await getContractClient(chain, normalized);
  const functionNames = dappClient.spec.funcs().map((fn: xdr.ScSpecFunctionV0) => fn.name().toString());
  const isTrustlineFirewall = isStellarTrustlineFirewallSpec(functionNames);

  let firewallTargetAddress: string | null = null;
  let specContractAddress = normalized;

  if (isTrustlineFirewall) {
    if (typeof (dappClient as any).target !== 'function') {
      throw new Error('Trustline Firewall detected but target() is not callable');
    }
    const targetTx = await (dappClient as any).target();
    firewallTargetAddress = normalizeStellarAddress(String(targetTx.result || ''));
    if (!firewallTargetAddress || !StrKey.isValidContract(firewallTargetAddress)) {
      throw new Error('Trustline Firewall target() did not return a valid contract address');
    }
    specContractAddress = firewallTargetAddress;
  }

  const validationEngineAddress = await readValidationEngineAddress(chain, normalized);

  const veClient = await getContractClient(chain, validationEngineAddress);
  if (typeof (veClient as any).registry !== 'function') {
    throw new Error('Resolved address is not a valid Validation Engine (missing registry())');
  }

  const registryTx = await (veClient as any).registry();
  const registryAddress = normalizeStellarAddress(String(registryTx.result || ''));
  if (!registryAddress || !StrKey.isValidContract(registryAddress)) {
    throw new Error('Validation Engine registry() did not return a valid contract address');
  }

  return {
    isTrustlineFirewall,
    specContractAddress,
    firewallTargetAddress,
    validationEngineAddress,
    registryAddress,
  };
}

/**
 * Resolve Validation Engine via instance storage key "VE",
 * then verify by calling `registry()` on the returned contract.
 */
export async function fetchAndVerifyStellarValidationEngine(
  chain: NetworkConfig,
  contractId: string
): Promise<{ validationEngineAddress: string; registryAddress: string }> {
  const result = await inspectStellarProtectedContract(chain, contractId);
  return {
    validationEngineAddress: result.validationEngineAddress,
    registryAddress: result.registryAddress,
  };
}

export async function readStellarValidationConfig(
  chain: NetworkConfig,
  validationEngineAddress: string
): Promise<{ trustlineEnabled: boolean; sanctionsEnabled: boolean }> {
  const veClient = await getContractClient(chain, validationEngineAddress);
  const [trustlineTx, sanctionsTx] = await Promise.all([
    (veClient as any).trustline_oracle_enabled(),
    (veClient as any).sanctions_oracle_enabled(),
  ]);
  return {
    trustlineEnabled: Boolean(trustlineTx.result),
    sanctionsEnabled: Boolean(sanctionsTx.result),
  };
}

export async function fetchStellarRegistryOracleKeys(
  chain: NetworkConfig,
  validationEngineAddress: string
): Promise<{ keys: string[]; keyToAddress: Record<string, string> }> {
  const veClient = await getContractClient(chain, validationEngineAddress);
  const registryTx = await (veClient as any).registry();
  const registryAddress = normalizeStellarAddress(String(registryTx.result || ''));
  if (!registryAddress) {
    throw new Error('No validation registry on this engine');
  }

  const registryClient = await getContractClient(chain, registryAddress);
  if (typeof (registryClient as any).get_all_records !== 'function') {
    throw new Error('Registry does not expose get_all_records()');
  }

  const recordsTx = await (registryClient as any).get_all_records();
  const result = recordsTx.result as [string[], string[]] | undefined;
  const keys = (result?.[0] || []).map(String);
  const addrs = (result?.[1] || []).map((a: string) => normalizeStellarAddress(String(a)));

  const keyToAddress: Record<string, string> = {};
  const oracleKeys: string[] = [];
  keys.forEach((key, i) => {
    if (!key) return;
    oracleKeys.push(key);
    if (addrs[i]) keyToAddress[key] = addrs[i];
  });

  return { keys: oracleKeys, keyToAddress };
}

export type StellarSignTransaction = (
  xdr: string,
  opts?: { networkPassphrase?: string; address?: string; network?: string }
) => Promise<{ signedTxXdr?: string; error?: { message?: string } } | string>;

/**
 * Submit set_validation_configuration on the Stellar Validation Engine.
 * Requires Freighter (or compatible) signTransaction callback.
 */
export async function submitStellarValidationConfiguration(params: {
  chain: NetworkConfig;
  validationEngineAddress: string;
  publicKey: string;
  signTransaction: StellarSignTransaction;
  trustlineEnabled: boolean;
  sanctionsEnabled: boolean;
  sanctionsKey: string | null;
}): Promise<void> {
  const {
    chain,
    validationEngineAddress,
    publicKey,
    signTransaction,
    trustlineEnabled,
    sanctionsEnabled,
    sanctionsKey,
  } = params;

  const veClient = await Client.from({
    contractId: normalizeStellarAddress(validationEngineAddress),
    networkPassphrase: networkPassphraseFor(chain),
    rpcUrl: chain.rpcUrl,
    publicKey,
    signTransaction: async (xdr: string, opts?: Record<string, unknown>) => {
      const result = await signTransaction(xdr, {
        networkPassphrase: networkPassphraseFor(chain),
        address: publicKey,
        ...(opts || {}),
      });
      if (typeof result === 'string') {
        return { signedTxXdr: result };
      }
      if (result.error) {
        throw new Error(result.error.message || 'Freighter rejected the transaction');
      }
      if (!result.signedTxXdr) {
        throw new Error('Freighter did not return a signed transaction');
      }
      return { signedTxXdr: result.signedTxXdr };
    },
  });

  const tx = await (veClient as any).set_validation_configuration({
    trustline_enabled: trustlineEnabled,
    sanctions_enabled: sanctionsEnabled,
    sanctions_key: sanctionsEnabled && sanctionsKey ? sanctionsKey : null,
  });

  await tx.signAndSend();
}
